import { usePageDestinationAccess } from 'src/app/AppAuth/usePageAccess'
import React from 'react'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import {
  ResourceWithAuthz,
  useHasRequiredCapabilities,
} from 'src/app/AppAuth/AppAuth.hooks'
import palette from 'src/designSystem/variables/palette'
import styled from 'styled-components/macro'
import { colorStyle, compose, typography } from 'styled-system'

export const TextButton = ({
  color,
  pageDestination,
  disabled,
  onClick,
  onMouseDown,
  requiredCapabilities,
  resource,
  children,
  style,
}: {
  color?: string
  pageDestination?: string
  disabled?: boolean
  onClick: (e: React.MouseEvent<HTMLElement>) => void
  onMouseDown?: (e: React.MouseEvent<HTMLElement>) => void
  requiredCapabilities?: CAPABILITY[]
  resource?: ResourceWithAuthz
  children?: JSX.Element | string | (JSX.Element | string)[]
  style?: React.CSSProperties
}): JSX.Element => {
  const hasCapabilities = useHasRequiredCapabilities(
    requiredCapabilities,
    resource,
  )
  const canOpen = usePageDestinationAccess()
  if (pageDestination && !canOpen(pageDestination))
    return <span style={style}>{children}</span>
  const isAuthorized = hasCapabilities

  let isButtonDisabled = disabled
  if (!isAuthorized) {
    isButtonDisabled = true
  }

  return (
    <StyledTextButton
      color={color}
      disabled={isButtonDisabled}
      onClick={onClick}
      onMouseDown={onMouseDown}
      style={style}
    >
      {children}
    </StyledTextButton>
  )
}

export const StyledTextButton = styled.button.attrs<{
  color: string
  disabled?: boolean
}>({
  colors: 'link.primary',
  fontSize: 3,
  type: 'button',
})`
  ${compose(colorStyle, typography)}
  border: none;
  display: flex;
  flex-direction: row;
  gap: 0.5em;
  justify-content: center;
  align-items: center;
  padding: 0;
  background-color: transparent;
  text-decoration: underline;
  cursor: pointer;
  ${({ color }) => color && `color: ${color};`};
  ${({ disabled, color }) =>
    disabled &&
    `:hover {color: ${color || palette.neutral['900']};} cursor: not-allowed`};
`

export const BlueTextButton = styled(StyledTextButton)`
  color: ${palette.blue[600]};
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  &:hover {
    color: ${palette.blue[700]};
  }
`

export const AddTextButton = styled(BlueTextButton)`
  display: inline-flex;
  text-transform: uppercase;
  letter-spacing: 0.05px;
  line-height: 100%;
`
